// src/components/AITrackingManager.jsx
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import styled, { keyframes } from 'styled-components';

// ====== TITANIUM GRADIENT ANIMATION ======
const titaniumGradient = keyframes`
  0%   { background-position: 0% 50%; }
  25%  { background-position: 100% 50%; }
  50%  { background-position: 100% 50%; }
  75%  { background-position: 0% 50%; }
  100% { background-position: 0% 50%; }
`;

const Container = styled.div`
  width: 100%;
  min-height: 100vh;
  background: #000;
  color: #fff;
  padding: 2rem 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 140px;
`;

const Title = styled.h1`
  font-size: clamp(2.2rem, 6vw, 3.5rem);
  font-weight: 900;
  text-align: center;
  background: linear-gradient(
    90deg,
    #a0a0a0 0%, #e0e0e0 10%, #c0c0c0 20%, #f0f0f0 30%,
    #d0d0d0 40%, #fff 50%, #d0d0d0 60%, #f0f0f0 70%,
    #c0c0c0 80%, #e0e0e0 90%, #a0a0a0 100%
  );
  background-size: 300% 300%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: ${titaniumGradient} 12s ease-in-out infinite;
  margin-bottom: 2rem;
`;

const InputForm = styled.form`
  width: 100%;
  max-width: 900px;
  display: flex;
  gap: 0.8rem;
  margin-bottom: 3rem;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const Input = styled.input`
  flex: 1;
  padding: 1rem 1.2rem;
  font-size: 1.1rem;
  background: #111;
  border: 1px solid #333;
  border-radius: 12px;
  color: #fff;
  outline: none;

  &:focus {
    border-color: #00ccff;
    box-shadow: 0 0 0 3px rgba(0, 204, 255, 0.2);
  }
`;

const SubmitButton = styled.button`
  padding: 1rem 2rem;
  font-size: 1.1rem;
  font-weight: 600;
  background: #00ccff;
  color: #000;
  border: none;
  border-radius: 12px;
  cursor: pointer;

  &:hover {
    background: #00aaff;
  }

  &:disabled {
    background: #333;
    cursor: not-allowed;
  }
`;

const PlanGrid = styled.div`
  width: 100%;
  max-width: 900px;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const StepCard = styled(motion.div)`
  background: #111;
  border: 1px solid #222;
  border-radius: 20px;
  padding: 1.8rem;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 4px;
    background: linear-gradient(90deg, #00ccff, #0066ff);
    opacity: 0;
    transition: 0.3s;
  }

  &:hover {
    border-color: #00ccff;
    &::before { opacity: 1; }
  }
`;

const StepHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const StepNumber = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: #00ccff;
  color: #000;
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: 800;
`;

const StepTitle = styled.h3`
  margin: 0;
  font-size: 1.3rem;
`;

const StepContent = styled.p`
  margin-top: 1rem;
  color: #ddd;
`;

const ResearchSection = styled.div`
  width: 100%;
  max-width: 900px;
  margin-top: 3rem;
  text-align: center;
`;

const ResearchTitle = styled.h2`
  color: #00ccff;
  margin-bottom: 0.5rem;
`;

const ResearchList = styled.ul`
  list-style: none;
  padding: 0;
  margin-top: 2rem;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;

  @media (max-width: 1024px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 768px) { grid-template-columns: 1fr; }
`;

const ResearchItem = styled(motion.li)`
  a {
    display: block;
    padding: 1rem 1.2rem;
    background: #111;
    border: 1px solid #333;
    border-radius: 12px;
    color: #00ccff;
    text-decoration: none;

    &:hover {
      background: #1a1a1a;
      border-color: #00ccff;
      color: #fff;
    }
  }
`;

// ====== FRONTEND → BACKEND REQUEST ======
const fetchPlanFromBackend = async (goal) => {
  try {
    const res = await fetch("http://localhost:5000/api/generate", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ goal })
    });

    const data = await res.json();
    return data.plan;
  } catch (err) {
    console.error("Backend error", err);
    return null;
  }
};

const AITrackingManager = () => {
  const [input, setInput] = useState('');
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [count, setCount] = useState(0);

  const submit = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;

    if (count >= 3) return alert("3 free plans used. Upgrade in app");

    setLoading(true);

    const result = await fetchPlanFromBackend(input);

    if (result) {
      setPlan(result);
      setCount(count + 1);
    }

    setLoading(false);
  };

  return (
    <Container>
      <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>
        <Title>Turn Your Dream Into a Plan</Title>
      </motion.div>

      <InputForm onSubmit={submit}>
        <Input
          placeholder="I want to..."
          value={input}
          onChange={(e) => setInput(e.target.value)}
          disabled={loading}
        />
        <SubmitButton disabled={loading}>
          {loading ? 'Creating...' : 'Plan My Dream'}
        </SubmitButton>
      </InputForm>

      {plan && (
        <>
          <PlanGrid>
            {plan.steps.map((s, i) => (
              <StepCard key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}>
                <StepHeader>
                  <StepNumber>{s.step}</StepNumber>
                  <StepTitle>{s.title}</StepTitle>
                </StepHeader>
                <StepContent>{s.task}</StepContent>
              </StepCard>
            ))}
          </PlanGrid>

          <ResearchSection>
            <ResearchTitle>Полезные ресурсы</ResearchTitle>
            <ResearchList>
              {plan.research.map((link, idx) => (
                <ResearchItem key={idx} initial={{ opacity: 0 }} whileInView={{ opacity: 1 }}>
                  <a href={link} target="_blank" rel="noreferrer">
                    {link.replace("https://", "").split("/")[0]}
                  </a>
                </ResearchItem>
              ))}
            </ResearchList>
          </ResearchSection>
        </>
      )}
    </Container>
  );
};

export default AITrackingManager;
